import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getSubscriptionStatus, type SubscriptionStatus } from '../api/subscription.api';

interface SubscriptionContextValue {
  status: SubscriptionStatus | null;
  isLoading: boolean;
  error: string | null;
  /** Whether the account has active access (paid, trial, or grace period) */
  isActive: boolean;
  isTrial: boolean;
  isExpired: boolean;
  isPastDue: boolean;
  refetch: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextValue>({
  status: null,
  isLoading: true,
  error: null,
  isActive: true, // fail-open default
  isTrial: false,
  isExpired: false,
  isPastDue: false,
  refetch: () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getSubscriptionStatus();
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check subscription');
      // Fail-open: if we can't check, allow access
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const value: SubscriptionContextValue = {
    status,
    isLoading,
    error,
    // Fail-open: if status is null (error or loading), allow access
    isActive: status?.isActive ?? true,
    isTrial: status?.isTrial ?? false,
    isExpired: status?.subscriptionStatus === 'expired',
    isPastDue: status?.subscriptionStatus === 'past_due',
    refetch: fetchStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
